import { Button, makeStyles, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { AjaxResult } from "../../../enums/ajaxResult";
import { ApiResult } from "../../../rest/baseApi";
import { ValidationErrors } from "../validationErrors";
import { ButtonWithConfirm } from "../../ButtonWithConfirm";

interface Props {
    label: string;
    onUploadSuccess: () => void;
    disabled: boolean;
    loadedLogo: string | undefined;
    loadingLogo: boolean;
    uploadMethod: (data: FormData) => Promise<ApiResult<unknown>>
    deleteMethod: () => Promise<ApiResult<unknown>>
}

export const LogoUploadSection = ({
    label,
    onUploadSuccess,
    disabled,
    loadedLogo,
    loadingLogo,
    uploadMethod,
    deleteMethod,
}: Props) => {
    const classes = useStyles();
    const fileRef: React.RefObject<HTMLInputElement> = React.useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const [preview, setPreview] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const clearChosenFile = useCallback(() => {
        if (fileRef.current) {
            fileRef.current.value = "";
        }
        setPreview(null);
    }, []);

    const handleDelete = useCallback(() => {
        setDeleting(true);
        deleteMethod().then(r => {
            if (r.result === AjaxResult.Success) {
                enqueueSnackbar(`Successfully deleted ${label}`, { variant: "success" });
                onUploadSuccess();
            } else {
                enqueueSnackbar(`Failed to delete ${label}`, { variant: "error" });
            }
            setDeleting(false);
        });
    }, [enqueueSnackbar, onUploadSuccess, label, deleteMethod]);

    const handleUpload = useCallback(() => {
        if (!fileRef.current || !fileRef.current.files || !fileRef.current.files[0]) return;

        setValidationErrors([]);
        setUploading(true);

        const data = new FormData();
        data.append('file', fileRef.current.files[0]);

        uploadMethod(data).then(r => {
            if (r.result === AjaxResult.Success) {
                enqueueSnackbar(`Successfully uploaded ${label}`, { variant: "success" });
                onUploadSuccess();
                clearChosenFile();
            } else {
                if (r.messages) {
                    setValidationErrors(r.messages);
                }
                enqueueSnackbar(`Failed to upload ${label}`, { variant: "error" });
            }
            setUploading(false);
        });
    }, [enqueueSnackbar, onUploadSuccess, label, clearChosenFile, uploadMethod]);

    const editDisabled = disabled || uploading;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0];
        setValidationErrors([]);
        setPreview(null);

        if (!file || !fileRef.current) {
            return;
        }

        if (file.type !== "image/png") {
            setValidationErrors(["File must be a PNG image."]);
            fileRef.current.value = "";
            return;
        }
        // [PAS-904] file.type only reflects the .png extension, so a renamed .exe
        // passes that check. Try to decode the file as an image — if the browser
        // can't, the contents are not a valid PNG and we reject here with a clear
        // message so the user sees what's wrong instead of a dead "No File Chosen".
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            setPreview(objectUrl);
        };
        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            setValidationErrors([
                "The selected file is not a valid PNG image. Its contents don't match the .png extension.",
            ]);
            if (fileRef.current) {
                fileRef.current.value = "";
            }
        };
        img.src = objectUrl;
    };

    if (loadingLogo) {
        return (
            <Typography variant="body2">
                {`Loading ${label}`}
            </Typography>
        );
    }

    const modifying = uploading || deleting;

    return (
        <div className={classes.sectionContainer}>
            {!loadingLogo && loadedLogo
                ? (
                    <>
                        <Typography variant="body2">
                            {`${label}:`}
                        </Typography>
                        <img
                            src={loadedLogo}
                            alt="Selected File Preview"
                            className={classes.preview}
                        />
                        <div className={classes.flexContainer}>
                            <ButtonWithConfirm
                                variant="contained"
                                color="secondary"
                                size="small"
                                disabled={modifying}
                                confirmedCallback={handleDelete}
                                confirmDialogContent={`Are you sure you want to remove this ${label}?`}
                            >
                                Remove
                            </ButtonWithConfirm>
                        </div>
                    </>
                )
                : (
                    <Typography variant="body2">
                        {`No ${label} uploaded`}
                    </Typography>
                )
            }

            <Typography variant="body2">
                {`Upload New ${label}:`}
            </Typography>

            {validationErrors.length > 0 && (
                <div><ValidationErrors errors={validationErrors} /></div>
            )}

            {preview
                ? (<>
                    <img
                        src={preview}
                        alt="Selected File Preview"
                        className={classes.preview}
                    />

                    <div className={classes.flexContainer}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleUpload}
                            disabled={editDisabled}
                        >
                            {uploading ? "Uploading..." : "Upload"}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            disabled={modifying}
                            onClick={clearChosenFile}
                        >
                            Cancel
                        </Button>
                    </div>
                </>)
                : (<>
                    <div className={classes.flexContainer}>
                        <Button
                            variant="outlined"
                            color="default"
                            size="small"
                            onClick={() => fileRef.current && fileRef.current.click()}>
                            Choose File
                        </Button>
                        <Typography variant="body2">
                            {`No File Chosen (PNG only, up to 200KB)`}
                        </Typography>
                    </div>
                </>)
            }

            <input
                id="logo_image"
                type="file"
                ref={fileRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
            />
        </div>
    );
};

const useStyles = makeStyles({
    sectionContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    flexContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        alignItems: "center",
    },
    preview: {
        maxWidth: "400px",
        maxHeight: "200px",
        alignSelf: "start",
        border: "1px solid #e7e7e7",
        padding: "4px",
    }
});
